/**
 * Alt Text Utilities
 *
 * Provides functions for generating consistent, accessible alt text
 * across the application following WCAG guidelines.
 */

import { Locale } from '@spot'

export const WEATHER_CONDITIONS: Record<string, Record<Locale, string>> = {
  '00': {
    de: 'Sonnig',
    en: 'Sunny',
    it: 'Soleggiato'
  },
  '01': {
    de: 'Leicht bewölkt',
    en: 'Partly cloudy',
    it: 'Parzialmente nuvoloso'
  },
  '02': {
    de: 'Bewölkt',
    en: 'Cloudy',
    it: 'Nuvoloso'
  },
  '03': {
    de: 'Stark bewölkt',
    en: 'Overcast',
    it: 'Molto nuvoloso'
  },
  '04': {
    de: 'Nebel',
    en: 'Foggy',
    it: 'Nebbia'
  },
  '05': {
    de: 'Leichter Regen',
    en: 'Light rain',
    it: 'Pioggia leggera'
  },
  '06': {
    de: 'Regen',
    en: 'Rain',
    it: 'Pioggia'
  },
  '07': {
    de: 'Starker Regen',
    en: 'Heavy rain',
    it: 'Pioggia forte'
  },
  '08': {
    de: 'Gewitter',
    en: 'Thunderstorm',
    it: 'Temporale'
  },
  '09': {
    de: 'Leichter Schnee',
    en: 'Light snow',
    it: 'Neve leggera'
  },
  '10': {
    de: 'Schnee',
    en: 'Snow',
    it: 'Neve'
  },
  '11': {
    de: 'Starker Schnee',
    en: 'Heavy snow',
    it: 'Neve forte'
  },
  '12': {
    de: 'Schneeschauer',
    en: 'Snow showers',
    it: 'Nevicate'
  },
  '13': {
    de: 'Regenschauer',
    en: 'Rain showers',
    it: 'Rovesci di pioggia'
  },
  '14': {
    de: 'Wechselhaft',
    en: 'Variable',
    it: 'Variabile'
  },
  '15': {
    de: 'Windig',
    en: 'Windy',
    it: 'Ventoso'
  },
  '16': {
    de: 'Sehr windig',
    en: 'Very windy',
    it: 'Molto ventoso'
  },
  '17': {
    de: 'Sturm',
    en: 'Stormy',
    it: 'Tempestoso'
  },
  '18': {
    de: 'Hagel',
    en: 'Hail',
    it: 'Grandine'
  },
  '19': {
    de: 'Eiskalt',
    en: 'Freezing',
    it: 'Gelido'
  },
  '20': {
    de: 'Heiß',
    en: 'Hot',
    it: 'Caldo'
  },
  '21': {
    de: 'Trocken',
    en: 'Dry',
    it: 'Secco'
  },
  '22': {
    de: 'Feucht',
    en: 'Humid',
    it: 'Umido'
  },
  '23': {
    de: 'Eisregen',
    en: 'Freezing rain',
    it: 'Pioggia gelata'
  },
  '24': {
    de: 'Schneematsch',
    en: 'Sleet',
    it: 'Nevischio'
  },
  '25': {
    de: 'Frost',
    en: 'Frost',
    it: 'Brina'
  },
  '26': {
    de: 'Glatteis',
    en: 'Ice',
    it: 'Ghiaccio'
  },
  '27': {
    de: 'Schneesturm',
    en: 'Blizzard',
    it: 'Tormenta di neve'
  },
  '28': {
    de: 'Sandsturm',
    en: 'Dust storm',
    it: 'Tempesta di sabbia'
  },
  '29': {
    de: 'Dunst',
    en: 'Haze',
    it: 'Foschia'
  },
  '30': {
    de: 'Klar',
    en: 'Clear',
    it: 'Sereno'
  }
}

export const LOGO_ALT_TEXTS: Record<string, Record<Locale, string>> = {
  surselva: {
    de: 'Surselva Logo, zur Startseite',
    en: 'Surselva Logo, back to homepage',
    it: 'Logo Surselva, torna alla homepage'
  }
}

/**
 * Generates alt text for weather icons
 */
export function getWeatherAltText(
  iconCode: string,
  locale: Locale = 'de'
): string {
  const code = iconCode.replace(/^weather_big_/, '').replace(/\.(png|svg)$/, '')

  const condition = WEATHER_CONDITIONS[code]
  if (!condition) {
    return ''
  }

  return condition[locale] || condition.de
}

/**
 * Generates alt text for project logos
 */
export function getLogoAltText(project: string, locale: Locale = 'de'): string {
  const logoText = LOGO_ALT_TEXTS[project]
  if (!logoText) {
    return `Logo, ${locale === 'de' ? 'zur Startseite' : locale === 'en' ? 'back to homepage' : 'torna alla homepage'}`
  }

  return logoText[locale] || logoText.de
}

/**
 * Determines if an image should be considered decorative
 * Based on content and context
 */
export function isDecorativeImage(
  alt: string | undefined,
  context: 'hero' | 'content' | 'gallery' | 'icon' | 'logo' = 'content'
): boolean {
  if (alt === '' || alt === 'decorative') {
    return true
  }

  if (alt && alt.trim().length > 0 && alt !== 'decorative') {
    return false
  }

  switch (context) {
    case 'hero':
      return true
    case 'logo':
      return false
    case 'icon':
      return false
    case 'gallery':
      return false
    default:
      return true
  }
}

/**
 * Validates alt text quality
 */
export function validateAltText(alt: string): {
  isValid: boolean
  warnings: string[]
} {
  const warnings: string[] = []

  if (!alt || alt.trim().length === 0) {
    return { isValid: true, warnings: [] }
  }

  if (alt.toLowerCase().includes('image')) {
    warnings.push('Alt text should not contain "image"')
  }

  if (alt.toLowerCase().includes('picture')) {
    warnings.push('Alt text should not contain "picture"')
  }

  if (alt.toLowerCase().includes('photo')) {
    warnings.push('Alt text should not contain "photo"')
  }

  if (alt === 'decorative') {
    warnings.push('Use empty string instead of "decorative"')
  }

  if (alt.length > 150) {
    warnings.push('Alt text should be concise (under 150 characters)')
  }

  if (alt.length < 3 && alt !== '') {
    warnings.push('Alt text should be descriptive or empty')
  }

  return {
    isValid: warnings.length === 0,
    warnings
  }
}

/**
 * Generates context-aware alt text for common image types
 */
export function generateAltText(
  type: 'event' | 'accommodation' | 'story' | 'nature' | 'culture',
  title?: string,
  description?: string,
  locale: Locale = 'de'
): string {
  const templates = {
    event: {
      de: title ? `Veranstaltung: ${title}` : 'Veranstaltungsbildd',
      en: title ? `Event: ${title}` : 'Event image',
      it: title ? `Evento: ${title}` : 'Immagine evento'
    },
    accommodation: {
      de: title ? `Unterkunft: ${title}` : 'Unterkunftsbild',
      en: title ? `Accommodation: ${title}` : 'Accommodation image',
      it: title ? `Alloggio: ${title}` : 'Immagine alloggio'
    },
    story: {
      de: title ? `Geschichte: ${title}` : 'Geschichtenbild',
      en: title ? `Story: ${title}` : 'Story image',
      it: title ? `Storia: ${title}` : 'Immagine storia'
    },
    nature: {
      de: title ? `Natur: ${title}` : 'Naturbild',
      en: title ? `Nature: ${title}` : 'Nature image',
      it: title ? `Natura: ${title}` : 'Immagine natura'
    },
    culture: {
      de: title ? `Kultur: ${title}` : 'Kulturbild',
      en: title ? `Culture: ${title}` : 'Culture image',
      it: title ? `Cultura: ${title}` : 'Immagine cultura'
    }
  }

  return templates[type]?.[locale] || templates[type]?.de || ''
}
